// The ONE registration-defaults factory (ADR 0037). Every surface that creates
// a Registration with "sensible defaults" builds its payload here so the seeds
// can't diverge again: the admin CreateRegistrationDialog (attach to an
// existing competition), GetStartedAdmin's onboarding step 2, and the server's
// combined competition+registration create (organization.controller.ts:
// onboardingBuild → createRegistrationForCompetition).
//
// ADR 0035 vocabulary: everything stamped by this factory is a FORM SEED — a
// deliberate value written onto the new registration/parent — NOT a platform
// default. Platform defaults live only in CONFIG_DEFAULTS and are supplied on
// the read side; unset means inherit.
import { CONFIG_DEFAULTS } from './configDefaults';
import { isIndividualSport } from './sportCategory';

// Default field shapes for a new registration. Consolidated from the retired
// create-registration wizard's CreateTournament/initialData.ts and
// CreateLeague/initialData.ts (previously apps/admin/src/utils/
// registrationDefaults.ts — moved here so the API can share them).
export const INITIAL_PLAYER_FIELDS = [
  {
    name: 'First Name',
    fieldId: 'first_name',
    type: 'text',
    required: true,
    helperText: 'Enter your first name',
    primitiveType: 'string',
    fieldType: 'textbox',
    error: { name: '', helperText: '' },
    permanent: true,
    pinned: true,
  },
  {
    name: 'Last Name',
    fieldId: 'last_name',
    type: 'text',
    required: true,
    helperText: 'Enter your last name',
    fieldType: 'textbox',
    primitiveType: 'string',
    error: { name: '', helperText: '' },
    permanent: true,
    pinned: true,
  },
];

export const INITIAL_TEAM_FIELDS = [
  {
    name: 'Team Name',
    fieldId: 'team_name',
    type: 'text',
    required: true,
    helperText: 'Enter your teams name',
    primitiveType: 'string',
    fieldType: 'textbox',
    error: { name: '', helperText: '' },
    permanent: true,
  },
];

export interface SportRosterSeeds {
  minPlayers: number;
  maxPlayers: number;
  excludeTeamName: boolean;
  requireCoaches: boolean;
}

// Sport-informed roster seeds (ported from CreateRegistrationDialog's
// sportDefaults / the wizard's applySportDefaults). FORM SEEDS, not platform
// defaults — the platform defaults are minPlayers 1 / maxPlayers unlimited /
// excludeTeamName false / requireCoaches true.
//
// requireCoaches:false for individual sports is deliberate: the public team
// step treats an UNSET requireCoaches as true (TournamentTeamDetails), which
// would block a doubles/pickleball registrant on a coach they don't have. The
// pickleball demo seed already stamps false; this makes it the rule.
export function getSportRosterSeeds(sport?: string | null): SportRosterSeeds {
  const key = (sport || '').toLowerCase();
  if (key === 'basketball') {
    return { minPlayers: 5, maxPlayers: 15, excludeTeamName: false, requireCoaches: CONFIG_DEFAULTS.requireCoaches };
  }
  // Volleyball/soccer numbers MUST stay in sync with the admin Settings form's
  // SPORTS_CONFIG (apps/admin .../Settings/SettingsForm.tsx) — the two surfaces
  // seed the same roster rules and diverging them ships contradictory forms.
  if (key === 'volleyball') {
    return { minPlayers: 6, maxPlayers: 12, excludeTeamName: false, requireCoaches: CONFIG_DEFAULTS.requireCoaches };
  }
  if (key === 'soccer') {
    return { minPlayers: 5, maxPlayers: 11, excludeTeamName: false, requireCoaches: CONFIG_DEFAULTS.requireCoaches };
  }
  if (isIndividualSport(key)) {
    return { minPlayers: 1, maxPlayers: 2, excludeTeamName: true, requireCoaches: false };
  }
  return { minPlayers: 1, maxPlayers: 15, excludeTeamName: false, requireCoaches: CONFIG_DEFAULTS.requireCoaches };
}

// The hierarchy roster keys mirrored onto the parent competition's config at
// create time (the parent is the authoritative home — ADR 0034). Deliberately
// EXCLUDES the registration-own keys (gates, showAllRegistrationTeams,
// playerAutoComplete): those must never land on a tournament/league config
// tier.
export const PARENT_MIRROR_CONFIG_KEYS = [
  'requirePlayers',
  'requireCoaches',
  'requireTeamRepresentatives',
  'excludeTeamName',
  'minCoaches',
  'maxCoaches',
  'minPlayers',
  'maxPlayers',
  'minTeamRepresentatives',
  'maxTeamRepresentatives',
] as const;

export interface RegistrationSeedInput {
  sport?: string | null;
  /**
   * When attaching to an EXISTING competition, its current config — parent
   * values win over the sport seeds so the create round-trips the parent's
   * settings instead of overwriting them.
   */
  parentConfig?: Record<string, unknown> | null;
  /** Applied last — e.g. GetStarted's { maxPlayers: N }. */
  overrides?: Record<string, unknown>;
}

export interface RegistrationSeed {
  allowCoaches: boolean;
  fields: typeof INITIAL_PLAYER_FIELDS;
  teamFields: never[];
  playerWaivers: never[];
  config: Record<string, unknown>;
}

const pickSet = (parentConfig: Record<string, unknown>, key: string, fallback: unknown) => {
  const v = parentConfig[key];
  return v === null || v === undefined ? fallback : v;
};

export function buildRegistrationSeed({ sport, parentConfig, overrides }: RegistrationSeedInput): RegistrationSeed {
  const seeds = getSportRosterSeeds(sport);
  const parent = parentConfig || {};

  const config: Record<string, unknown> = {
    // Hierarchy keys — parent's current value wins, else FORM SEED.
    requirePlayers: pickSet(parent, 'requirePlayers', CONFIG_DEFAULTS.requirePlayers),
    requireCoaches: pickSet(parent, 'requireCoaches', seeds.requireCoaches),
    requireTeamRepresentatives: pickSet(parent, 'requireTeamRepresentatives', CONFIG_DEFAULTS.requireTeamRepresentatives),
    excludeTeamName: pickSet(parent, 'excludeTeamName', seeds.excludeTeamName),
    minCoaches: pickSet(parent, 'minCoaches', CONFIG_DEFAULTS.minCoaches),
    maxCoaches: pickSet(parent, 'maxCoaches', CONFIG_DEFAULTS.maxCoaches),
    minPlayers: pickSet(parent, 'minPlayers', seeds.minPlayers),
    maxPlayers: pickSet(parent, 'maxPlayers', seeds.maxPlayers),
    // 1/2 is a suggestion stamped at create, deliberately NOT the platform
    // 0/5 — FORM SEED, not a platform default (ADR 0035).
    minTeamRepresentatives: pickSet(parent, 'minTeamRepresentatives', 1),
    maxTeamRepresentatives: pickSet(parent, 'maxTeamRepresentatives', 2),
    // Registration-own keys (ADR 0034 — read directly off registration.config,
    // never hierarchy-resolved, never mirrored to the parent).
    playerAutoComplete: false,
    showAllRegistrationTeams: false,
    gateByStartDate: false,
    gateByEndDate: false,
    ...(overrides || {}),
  };

  return {
    allowCoaches: config.requireCoaches !== false,
    fields: INITIAL_PLAYER_FIELDS,
    teamFields: [],
    playerWaivers: [],
    config,
  };
}
